import { useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import {
  ClipboardText,
  Camera,
  Plugs,
  MapPin,
  UsersThree,
  SlidersHorizontal,
  Package,
  HardDrives,
  CheckCircle,
  Circle,
  Printer,
  ArrowCounterClockwise,
  Info,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const STORAGE_KEY = "prep-checklist-v1";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const ICONS = { Camera, Plugs, MapPin, UsersThree, SlidersHorizontal, Package, HardDrives };

const CONTENT = {
  en: {
    page_title: "Shoot Preparation Checklist",
    page_sub: "A printable, tickable checklist to prepare every photo session with confidence — from gear and location to client, technical settings, logistics, and post-production. Check items off as you go; your progress is saved on this device.",
    download: "Download as PDF",
    generating: "Generating...",
    reset: "Reset checklist",
    progress: (d, t) => `${d} of ${t} completed`,
    howto_title: "How to use this guide",
    howto_intro: "This checklist helps you prepare your photo sessions effectively, making sure you have everything you need to capture your best images without setbacks. Use each section as a quick reference and personalize it to your needs and style.",
    howto_steps: [
      { label: "Before the session", text: "Tick the boxes to make sure every pre-shoot detail is covered, from choosing the right gear to planning the location and communicating with the client." },
      { label: "During the session", text: "Refer to the checklist to confirm you've considered key aspects — lighting, posing, and technical settings — so you can focus on creativity." },
      { label: "After the session", text: "Use the final sections to review backup, initial editing, and delivery, keeping your workflow tidy and professional." },
    ],
    sections: [
      {
        id: "gear", icon: "Camera", title: "Gear Check",
        items: [
          { id: "batteries", label: "Batteries", text: "Charge all batteries and carry at least two spares." },
          { id: "cameras", label: "Cameras", text: "Have your main and backup camera(s) ready for the session." },
          { id: "lenses", label: "Lenses", text: "Bring the lenses you need (wide, telephoto, etc.). Clean and check they're in good shape." },
          { id: "cards", label: "Memory cards", text: "Empty and format your cards beforehand. Carry extra cards for more space." },
          { id: "tripod", label: "Tripod / stabilizers", text: "Check your tripod or stabilizer is in good condition and right for the session." },
          { id: "flash", label: "Flash & lights", text: "Verify the flash works and bring diffusers. If using external lights, make sure they're charged and working." },
          { id: "reflectors", label: "Reflectors", text: "Bring reflectors if needed, plus a helper or stand to hold them." },
        ],
      },
      {
        id: "accessories", icon: "Plugs", title: "Additional Accessories",
        items: [
          { id: "filters", label: "Filters", text: "Bring filters (polarizer, ND, UV) based on the session and light conditions." },
          { id: "radioflash", label: "Trigger / radio flash", text: "If using a trigger or radio flash, confirm it works and the batteries are charged." },
          { id: "chargers", label: "Chargers & adapters", text: "Bring all needed chargers and adapters, especially if you'll have power on location." },
          { id: "diffusers", label: "Diffusers & softbox", text: "If softening light, make sure you have all the diffusers or softboxes you need." },
          { id: "backdrop", label: "Backdrop", text: "For studio work, verify you have the right backdrop (white, black, color) and that it's clean and wrinkle-free." },
          { id: "reader", label: "Card reader & backup drive", text: "Bring a card reader plus a laptop or portable drive so you can back up cards during or right after the shoot." },
        ],
      },
      {
        id: "location", icon: "MapPin", title: "Location Check",
        items: [
          { id: "address", label: "Address & contact", text: "Confirm the exact location and contact the client or location manager to verify details." },
          { id: "permits", label: "Permits", text: "If the location requires permits, make sure you have them on the day." },
          { id: "lighttimes", label: "Light times", text: "Check sunrise, sunset, and golden hour times for outdoor sessions." },
          { id: "weather", label: "Weather conditions", text: "Check the forecast and prepare alternatives for bad weather on outdoor shoots." },
          { id: "space", label: "Space prep", text: "For indoor locations, verify it's tidy and free of background distractions." },
        ],
      },
      {
        id: "client", icon: "UsersThree", title: "Client Review",
        items: [
          { id: "concept", label: "Session concept", text: "Review the concept or style with the client to align with their expectations." },
          { id: "shotlist", label: "Shot list & inspiration", text: "Prepare a shot list and reference/mood images so you don't miss any key shots." },
          { id: "outfits", label: "Client outfits & props", text: "Confirm the client has outfits and props ready. Recommend colors or styles to fit the concept." },
          { id: "makeup", label: "Makeup & hair style", text: "If applicable, confirm with the client or the hair/makeup team that the style fits the concept." },
          { id: "contract", label: "Signed contract", text: "Make sure the client has signed the service contract before the session." },
          { id: "release", label: "Model / image release", text: "For portraits, have a model/image release ready to sign — separate from the service contract." },
        ],
      },
      {
        id: "technical", icon: "SlidersHorizontal", title: "Final Technical Check",
        items: [
          { id: "settings", label: "Camera settings", text: "Verify ISO, aperture, and shutter speed for the expected light at the location." },
          { id: "raw", label: "Shoot RAW & format cards", text: "Set the camera to RAW (or RAW+JPEG), confirm date/time, and format cards in-camera." },
          { id: "wb", label: "White balance", text: "Set white balance to match the light (natural, artificial, etc.) or the effect you want." },
          { id: "testshot", label: "Test shot", text: "Take a few test frames on location to verify exposure, color, and sharpness." },
          { id: "flashsetup", label: "Flash setup", text: "If using external flash, test it to confirm exposure and light angles are right." },
        ],
      },
      {
        id: "logistics", icon: "Package", title: "Logistics",
        items: [
          { id: "schedule", label: "Schedule", text: "Bring a timeline for the order of shots and how long you'll spend on each." },
          { id: "emergency", label: "Emergency kit", text: "Bring a basic kit: paper towels, water, tissues, and a small first-aid kit." },
          { id: "snacks", label: "Snacks & water", text: "Bring water and snacks to keep your energy up, especially on long sessions." },
          { id: "personal", label: "Personal & comfort", text: "Comfortable shoes, weather-appropriate clothing, ID, and a flashlight/headlamp for low light." },
        ],
      },
      {
        id: "after", icon: "HardDrives", title: "After the Session",
        items: [
          { id: "backup", label: "Back up in 2 places", text: "Copy every card to two locations (e.g. computer + external drive) BEFORE formatting any card." },
          { id: "organize", label: "Download & organize", text: "Import, rename, and file the images into a clear folder structure by client/date." },
          { id: "cull", label: "Selection (culling)", text: "Do a first pass to flag the keepers and remove blurred or duplicate frames." },
          { id: "edit", label: "Initial editing", text: "Apply basic edits (exposure, white balance, crop) to your selects." },
          { id: "deliver", label: "Deliver to client", text: "Export final files and deliver via gallery/link on the agreed timeline." },
          { id: "archive", label: "Archive & cloud copy", text: "Keep a final archived copy (and a cloud backup) before clearing your cards." },
        ],
      },
    ],
  },
  es: {
    page_title: "Checklist de Preparación",
    page_sub: "Un checklist imprimible y marcable para preparar cada sesión fotográfica con confianza — desde el equipo y la locación hasta el cliente, los ajustes técnicos, la logística y la post-producción. Marca los ítems a medida que avanzas; tu progreso se guarda en este dispositivo.",
    download: "Descargar como PDF",
    generating: "Generando...",
    reset: "Reiniciar checklist",
    progress: (d, t) => `${d} de ${t} completados`,
    howto_title: "¿Cómo usar esta guía?",
    howto_intro: "Este checklist te ayuda a preparar tus sesiones fotográficas de manera efectiva, asegurando que tengas todo lo necesario para capturar tus mejores imágenes sin contratiempos. Usa cada sección como una referencia rápida y personalízala según tus necesidades y estilo.",
    howto_steps: [
      { label: "Antes de la sesión", text: "Marca los casilleros para asegurarte de que todos los detalles previos estén cubiertos, desde la selección del equipo hasta la planificación de la locación y la comunicación con el cliente." },
      { label: "Durante la sesión", text: "Consulta el checklist para verificar que has considerado aspectos clave —iluminación, poses y configuraciones técnicas— y así enfocarte en tu creatividad." },
      { label: "Después de la sesión", text: "Usa las secciones finales para revisar el respaldo, la edición inicial y la entrega, manteniendo tu flujo de trabajo ordenado y profesional." },
    ],
    sections: [
      {
        id: "gear", icon: "Camera", title: "Revisión del Equipo",
        items: [
          { id: "batteries", label: "Baterías", text: "Carga todas las baterías y lleva al menos dos de repuesto." },
          { id: "cameras", label: "Cámaras", text: "Asegúrate de tener la(s) cámara(s) principal(es) y de respaldo lista(s) para la sesión." },
          { id: "lenses", label: "Lentes", text: "Lleva los lentes necesarios (gran angular, teleobjetivo, etc.). Límpialos y verifica que estén en buen estado." },
          { id: "cards", label: "Tarjetas de memoria", text: "Vacía y formatea las tarjetas antes de la sesión. Lleva tarjetas adicionales por si necesitas más espacio." },
          { id: "tripod", label: "Tripié o estabilizadores", text: "Verifica que el tripié o estabilizador esté en buen estado y sea el adecuado para la sesión." },
          { id: "flash", label: "Flash y luces", text: "Revisa que el flash funcione y lleva difusores. Si usas luces externas, asegúrate de que estén cargadas y funcionando." },
          { id: "reflectors", label: "Reflectores", text: "Lleva reflectores si son necesarios, y ten a alguien para sujetarlos o un soporte adecuado." },
        ],
      },
      {
        id: "accessories", icon: "Plugs", title: "Accesorios Adicionales",
        items: [
          { id: "filters", label: "Filtros", text: "Lleva filtros (polarizador, ND, UV) según el tipo de sesión y las condiciones de luz." },
          { id: "radioflash", label: "Control o Radio Flash", text: "Si usas un control o radio flash, asegúrate de que funcione bien y que las baterías estén cargadas." },
          { id: "chargers", label: "Cargadores y adaptadores", text: "Lleva todos los cargadores y adaptadores, especialmente si tendrás acceso a energía en la locación." },
          { id: "diffusers", label: "Difusores y softbox", text: "Si necesitas suavizar la luz, asegúrate de tener todos los difusores o cajas de luz necesarias." },
          { id: "backdrop", label: "Fondo de fotografía", text: "Si la sesión es en estudio, verifica que tienes el fondo correcto (blanco, negro, colores) y que esté limpio y sin arrugas." },
          { id: "reader", label: "Lector de tarjetas y respaldo", text: "Lleva un lector de tarjetas y una laptop o disco portátil para respaldar las tarjetas durante o justo después de la sesión." },
        ],
      },
      {
        id: "location", icon: "MapPin", title: "Revisión de la Locación",
        items: [
          { id: "address", label: "Dirección y contacto", text: "Verifica la dirección exacta y contacta al cliente o responsable de la locación para confirmar detalles." },
          { id: "permits", label: "Permisos", text: "Si la locación requiere permisos, asegúrate de tenerlos contigo el día de la sesión." },
          { id: "lighttimes", label: "Horarios de luz", text: "Revisa los horarios del amanecer, atardecer o las horas doradas si la sesión es al aire libre." },
          { id: "weather", label: "Condiciones climáticas", text: "Consulta el pronóstico y prepara alternativas en caso de mal tiempo si la sesión es al aire libre." },
          { id: "space", label: "Preparación del espacio", text: "Si es un lugar cerrado, verifica que esté ordenado y sin distracciones de fondo que afecten las fotos." },
        ],
      },
      {
        id: "client", icon: "UsersThree", title: "Revisión con el Cliente",
        items: [
          { id: "concept", label: "Concepto de la sesión", text: "Repasa el concepto o estilo con el cliente para asegurarte de estar alineado con sus expectativas." },
          { id: "shotlist", label: "Lista de tomas e inspiración", text: "Prepara una lista de tomas (shot list) e imágenes de referencia para no olvidar ninguna toma clave." },
          { id: "outfits", label: "Outfits y accesorios del cliente", text: "Confirma que el cliente tenga listos los outfits y accesorios. Recomienda colores o estilos según el concepto." },
          { id: "makeup", label: "Estilo de maquillaje y peinado", text: "Si aplica, confirma con el cliente o el equipo de maquillaje/peinado que el estilo esté alineado con el concepto." },
          { id: "contract", label: "Contrato o acuerdo firmado", text: "Asegúrate de que el cliente haya firmado el contrato o acuerdo de servicio antes de la sesión." },
          { id: "release", label: "Liberación de imagen (model release)", text: "Para retratos, ten lista una liberación de imagen para firmar — aparte del contrato de servicio." },
        ],
      },
      {
        id: "technical", icon: "SlidersHorizontal", title: "Última Revisión Técnica",
        items: [
          { id: "settings", label: "Ajustes de cámara", text: "Verifica los ajustes de ISO, apertura y velocidad según las condiciones de luz previstas." },
          { id: "raw", label: "Disparar en RAW y formatear", text: "Configura la cámara en RAW (o RAW+JPEG), confirma fecha/hora y formatea las tarjetas en la cámara." },
          { id: "wb", label: "Balance de blancos", text: "Configura el balance de blancos según la luz (natural, artificial, etc.) o el efecto que desees." },
          { id: "testshot", label: "Prueba de disparo", text: "Realiza algunos disparos de prueba en la locación para verificar exposición, color y nitidez." },
          { id: "flashsetup", label: "Configuración de flash", text: "Si usas flash externo, haz pruebas para asegurarte de que la exposición y los ángulos de luz sean adecuados." },
        ],
      },
      {
        id: "logistics", icon: "Package", title: "Detalles Logísticos",
        items: [
          { id: "schedule", label: "Cronograma", text: "Lleva un cronograma para saber el orden de las tomas y cuánto tiempo dedicarás a cada una." },
          { id: "emergency", label: "Kit de emergencias", text: "Lleva un kit básico: toallas de papel, agua, pañuelos y un pequeño botiquín." },
          { id: "snacks", label: "Snacks y agua", text: "Lleva agua y snacks para mantener tu energía, especialmente en sesiones largas." },
          { id: "personal", label: "Personal y comodidad", text: "Calzado cómodo, ropa según el clima, identificación y una linterna/headlamp para poca luz." },
        ],
      },
      {
        id: "after", icon: "HardDrives", title: "Después de la Sesión",
        items: [
          { id: "backup", label: "Respaldo en 2 lugares", text: "Copia cada tarjeta en dos lugares (ej. computadora + disco externo) ANTES de formatear cualquier tarjeta." },
          { id: "organize", label: "Descarga y organización", text: "Importa, renombra y archiva las imágenes en una estructura de carpetas clara por cliente/fecha." },
          { id: "cull", label: "Selección (culling)", text: "Haz una primera pasada para marcar las buenas y descartar tomas borrosas o duplicadas." },
          { id: "edit", label: "Edición inicial", text: "Aplica ediciones básicas (exposición, balance de blancos, recorte) a tus seleccionadas." },
          { id: "deliver", label: "Entrega al cliente", text: "Exporta los archivos finales y entrégalos por galería/enlace en el plazo acordado." },
          { id: "archive", label: "Archivo y copia en la nube", text: "Guarda una copia final archivada (y un respaldo en la nube) antes de borrar las tarjetas." },
        ],
      },
    ],
  },
};

export default function PrepChecklistPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `prep-checklist-${lang}.pdf`, [lang]);

  const allIds = useMemo(() => c.sections.flatMap((s) => s.items.map((it) => `${s.id}.${it.id}`)), [c]);
  const total = allIds.length;

  const [checked, setChecked] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...checked]));
    } catch {
      /* ignore */
    }
  }, [checked]);

  const done = allIds.filter((id) => checked.has(id)).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const toggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const reset = () => setChecked(new Set());

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="checklist-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ClipboardText weight="fill" className="w-6 h-6 text-[#F5A524]" />
                <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight">{c.page_title}</h1>
              </div>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={reset}
                variant="outline"
                className="border-white/15 bg-transparent text-[#A1A1AA] hover:bg-white/5 hover:text-white"
                data-testid="checklist-reset-btn"
              >
                <ArrowCounterClockwise className="w-4 h-4 mr-1.5" />
                {c.reset}
              </Button>
              <Button
                onClick={download}
                disabled={!ready}
                className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
                data-testid="checklist-download-btn"
              >
                <Printer className="w-4 h-4 mr-1.5" />
                {generating ? c.generating : c.download}
              </Button>
            </div>
          </motion.div>

          {/* Progress */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4" data-testid="checklist-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#A1A1AA] text-sm">{c.progress(done, total)}</span>
              <span className="text-[#F5A524] text-sm font-mono font-bold">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#F5A524] transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
          </motion.div>

          {/* How to use */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/30 rounded-xl p-6 space-y-3" data-testid="checklist-howto">
            <div className="flex items-center gap-2">
              <Info weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.howto_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.howto_intro}</p>
            <ol className="space-y-2">
              {c.howto_steps.map((s, i) => (
                <li key={i} className="text-[#A1A1AA] text-sm leading-relaxed flex gap-2">
                  <span className="text-[#F5A524] font-bold shrink-0">{i + 1}.</span>
                  <span><b className="text-white">{s.label}:</b> {s.text}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* Sections */}
          {c.sections.map((s) => {
            const Icon = ICONS[s.icon] || Camera;
            const sectionDone = s.items.filter((it) => checked.has(`${s.id}.${it.id}`)).length;
            return (
              <motion.div key={s.id} variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6 space-y-3" data-testid={`checklist-section-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
                    <h2 className="font-['Outfit'] text-white text-xl font-medium">{s.title}</h2>
                  </div>
                  <span className="text-[#52525B] text-xs font-mono">{sectionDone}/{s.items.length}</span>
                </div>
                <div className="space-y-1.5">
                  {s.items.map((it) => {
                    const key = `${s.id}.${it.id}`;
                    const isOn = checked.has(key);
                    return (
                      <button
                        key={it.id}
                        onClick={() => toggle(key)}
                        className={`w-full text-left flex gap-3 items-start rounded-lg p-3 border transition-colors ${isOn ? "bg-[#F5A524]/10 border-[#F5A524]/30" : "bg-[#0A0A0A] border-white/10 hover:border-white/25"}`}
                        data-testid={`checklist-item-${s.id}-${it.id}`}
                      >
                        {isOn ? (
                          <CheckCircle weight="fill" className="w-5 h-5 text-[#F5A524] shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="w-5 h-5 text-[#52525B] shrink-0 mt-0.5" />
                        )}
                        <span>
                          <span className={`text-sm font-medium ${isOn ? "text-white line-through decoration-[#F5A524]/60" : "text-white"}`}>{it.label}</span>
                          <span className="block text-[#A1A1AA] text-xs leading-relaxed mt-0.5">{it.text}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Hidden printable version (clean, ink-light, print-ready) */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-2 text-sm">www.dslradvisor.com</p>
        <p className="text-right text-xs text-gray-500 mb-4">{lang === "es" ? "Fecha:" : "Date:"} ____________</p>

        <div className="mb-5 break-inside-avoid">
          <h2 className="text-lg font-bold mb-1 border-b-2 border-gray-300 pb-1">{c.howto_title}</h2>
          <p className="text-xs text-gray-700 mb-1">{c.howto_intro}</p>
          <ol className="list-decimal ml-5 space-y-0.5">
            {c.howto_steps.map((s, i) => (
              <li key={i} className="text-xs text-gray-700"><strong>{s.label}:</strong> {s.text}</li>
            ))}
          </ol>
        </div>

        {c.sections.map((s) => (
          <div key={s.id} className="mb-4 break-inside-avoid">
            <h2 className="text-lg font-bold mb-1 border-b-2 border-gray-200 pb-1">{s.title}</h2>
            <ul className="space-y-1">
              {s.items.map((it) => (
                <li key={it.id} className="text-xs text-gray-700 flex gap-2">
                  <span className="font-bold">☐</span>
                  <span><strong>{it.label}:</strong> {it.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
